import React, { useState } from "react";
import { toast } from "sonner";
import { FileText, Download, Send, ArrowUpRight, Users } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { scrollToId } from "@/components/SmoothScroll";
import api, { formatApiErrorDetail } from "@/lib/api";
import { RESOURCES, COMPANY } from "@/lib/content";
import usePageMeta from "@/lib/useMeta";

function TextField({ label, value, onChange, testid, type = "text", required }) {
  return (
    <div>
      <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">{label}{required && <span className="text-wcc-red"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} required={required}
        className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
    </div>
  );
}

export default function Resources() {
  usePageMeta(
    "Contractor Resources | West Coral Communications — COI, W-9 & Prequalification",
    "Prequalification documentation for prime contractors: Certificate of Insurance (COI within 24 hours), W-9, capability statement, equipment list, safety program and service-area map. Subcontractor & crew inquiries welcome."
  );
  const [req, setReq] = useState({ name: "", company: "", email: "", phone: "", subject: "Prequalification Package Request", message: "", hp: "" });
  const [sub, setSub] = useState({ type: "subcontractor", full_name: "", company_name: "", email: "", phone: "", location: "", crew_types: "", experience_notes: "", hp: "" });
  const [l1, setL1] = useState(false);
  const [l2, setL2] = useState(false);

  const setR = (k) => (e) => setReq((f) => ({ ...f, [k]: e.target.value }));
  const setS = (k) => (e) => setSub((f) => ({ ...f, [k]: e.target.value }));

  const requestDoc = (docName) => {
    setReq((r) => ({ ...r, subject: `Request: ${docName}`, message: `Please send the ${docName}.` }));
    setTimeout(() => scrollToId("request-form"), 60);
    toast.info(`Request pre-filled for "${docName}".`);
  };

  const submitReq = async (e) => {
    e.preventDefault();
    if (!req.name) return toast.error("Please enter your name.");
    setL1(true);
    try {
      await api.post("/contact", req);
      toast.success("Request received. We’ll send your documentation shortly.");
      setReq({ name: "", company: "", email: "", phone: "", subject: "Prequalification Package Request", message: "", hp: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    } finally { setL1(false); }
  };

  const submitSub = async (e) => {
    e.preventDefault();
    if (!sub.full_name) return toast.error("Please enter your name.");
    setL2(true);
    try {
      await api.post("/applications", sub);
      toast.success("Thanks! We’ll reach out to discuss your crew or subcontracting services.");
      setSub({ type: "subcontractor", full_name: "", company_name: "", email: "", phone: "", location: "", crew_types: "", experience_notes: "", hp: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    } finally { setL2(false); }
  };

  return (
    <div className="pt-[74px]">
      <section className="relative bg-wcc-navy text-white py-24 sm:py-28 overflow-hidden" data-testid="resources-hero">
        <img src="/assets/field-2.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-t from-wcc-navy via-wcc-navy/85 to-wcc-navy/70" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-6">Contractor Resources</p>
          <h1 className="font-display text-4xl sm:text-6xl font-semibold tracking-tighter uppercase leading-[0.92]">
            <MaskLine delay={0.1}>Everything primes</MaskLine>
            <MaskLine delay={0.22} className="text-wcc-red">need to onboard us.</MaskLine>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            Prequalification documentation, insurance certificates, and compliance records — provided fast so your
            project can mobilize without delays.
          </p>
        </div>
      </section>

      <section className="bg-wcc-paper py-24" data-testid="resources-grid">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-10">Available Documentation</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {RESOURCES.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.04}>
                <div className="group bg-white border border-wcc-ink/10 p-7 h-full flex flex-col hover:border-wcc-red transition-colors">
                  <FileText className="w-8 h-8 text-wcc-red mb-5" />
                  <h3 className="font-display text-lg font-medium text-wcc-ink mb-2">{r.name}</h3>
                  <p className="text-sm text-wcc-ink/60 leading-relaxed flex-1">{r.desc}</p>
                  <button onClick={() => requestDoc(r.name)} data-testid={`resource-request-${i}`}
                    className="mt-5 inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-widest text-wcc-navy group-hover:text-wcc-red transition-colors">
                    <Download className="w-3.5 h-3.5" /> Request
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 border-y border-wcc-ink/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
          {/* Documentation request */}
          <div id="request-form">
            <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-4">Request Documentation</p>
            <h2 className="font-display text-3xl font-medium tracking-tight text-wcc-ink mb-6">Prequalification Package</h2>
            <form onSubmit={submitReq} className="bg-wcc-paper border border-wcc-ink/10 p-8" data-testid="resource-request-form">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={req.hp} onChange={setR("hp")} style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
              <div className="grid sm:grid-cols-2 gap-5">
                <TextField label="Name" required value={req.name} onChange={setR("name")} testid="req-name" />
                <TextField label="Company" value={req.company} onChange={setR("company")} testid="req-company" />
                <TextField label="Email" type="email" value={req.email} onChange={setR("email")} testid="req-email" />
                <TextField label="Phone" value={req.phone} onChange={setR("phone")} testid="req-phone" />
              </div>
              <div className="mt-5">
                <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Documents Needed</label>
                <input value={req.subject} onChange={setR("subject")} data-testid="req-subject"
                  className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
              </div>
              <div className="mt-5">
                <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Notes</label>
                <textarea value={req.message} onChange={setR("message")} rows={3} data-testid="req-message"
                  className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink resize-none transition-colors" />
              </div>
              <button type="submit" disabled={l1} data-testid="req-submit"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-wcc-red text-white font-semibold px-6 py-3.5 rounded-sm hover:bg-wcc-red-deep disabled:opacity-60 transition-colors">
                {l1 ? "Sending…" : <>Send Request <Send className="w-4 h-4" /></>}
              </button>
            </form>
          </div>

          {/* Subcontractor inquiry */}
          <div>
            <p className="mono text-[11px] uppercase tracking-[0.3em] text-wcc-red mb-4">Crews & Subcontractors</p>
            <h2 className="font-display text-3xl font-medium tracking-tight text-wcc-ink mb-6">Work With Us</h2>
            <form onSubmit={submitSub} className="bg-wcc-paper border border-wcc-ink/10 p-8" data-testid="subcontractor-form">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={sub.hp} onChange={setS("hp")} style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
              <div className="grid sm:grid-cols-2 gap-5">
                <TextField label="Your Name" required value={sub.full_name} onChange={setS("full_name")} testid="sub-name" />
                <TextField label="Company Name" value={sub.company_name} onChange={setS("company_name")} testid="sub-company" />
                <TextField label="Email" type="email" value={sub.email} onChange={setS("email")} testid="sub-email" />
                <TextField label="Phone" value={sub.phone} onChange={setS("phone")} testid="sub-phone" />
                <TextField label="Location / Region" value={sub.location} onChange={setS("location")} testid="sub-location" />
                <TextField label="Crew Types (plow, trench, boring…)" value={sub.crew_types} onChange={setS("crew_types")} testid="sub-crew-types" />
              </div>
              <div className="mt-5">
                <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Experience, Equipment & Compliance</label>
                <textarea value={sub.experience_notes} onChange={setS("experience_notes")} rows={3} data-testid="sub-notes"
                  className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink resize-none transition-colors" />
              </div>
              <button type="submit" disabled={l2} data-testid="sub-submit"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-wcc-navy text-white font-semibold px-6 py-3.5 rounded-sm hover:bg-wcc-red disabled:opacity-60 transition-colors">
                {l2 ? "Sending…" : <>Submit Crew Info <Users className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-wcc-navy text-white py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-white/80">Questions about compliance or onboarding? Email us directly.</p>
          <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 bg-white text-wcc-navy font-semibold px-6 py-3 rounded-sm hover:bg-wcc-red hover:text-white transition-colors" data-testid="resources-email-cta">
            {COMPANY.email} <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
